import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ExternalLink, Cpu, Gauge, Globe2, CalendarClock, ArrowRight } from "lucide-react";
import api from "@/lib/api";
import { mediaUrl } from "@/lib/api";

const katresChips = [
  "Uscătoare & camere de uscare a cherestelei",
  "Tratament termic al lemnului",
  "Uscător progresiv KAPD",
  "Linia Nordic",
  "Prezent la LIGNA Hannover",
];

export default function Partners() {
  const [partners, setPartners] = useState([]);
  useEffect(() => {
    api.get("/partners").then((r) => setPartners(r.data)).catch(() => {});
  }, []);

  if (partners.length === 0) return null;
  const featured = partners.find((p) => p.featured) || partners[0];
  const others = partners.filter((p) => p.id !== featured.id);

  return (
    <section className="bg-slate-950 text-white border-t border-slate-800" data-testid="partners-section">
      <div className="max-w-7xl mx-auto px-6 py-24">
        <div className="flex items-center gap-3 mb-12">
          <span className="h-px w-12 bg-orange-600" />
          <span className="text-xs uppercase tracking-[0.3em] font-bold text-orange-500">Parteneri tehnologici</span>
        </div>

        {/* Featured partner */}
        <div className="border border-slate-800 bg-slate-900/40" data-testid="partner-featured">
          <div className="grid lg:grid-cols-3">
            <div className="p-8 lg:p-10 border-b lg:border-b-0 lg:border-r border-slate-800 flex flex-col justify-between">
              <div>
                {featured.logo ? (
                  <img src={mediaUrl(featured.logo)} alt={featured.name} className="h-12 object-contain object-left" />
                ) : (
                  <div className="text-4xl font-black tracking-tighter">{featured.name}<span className="text-orange-500">.</span></div>
                )}
                <span className="inline-flex items-center gap-2 border border-orange-900/60 bg-orange-950/30 text-orange-400 text-[11px] font-bold uppercase tracking-wider px-3 py-1.5 mt-5">
                  Reprezentat oficial în România de OXO
                </span>
              </div>
              {featured.website && (
                <a href={featured.website} target="_blank" rel="noreferrer" className="inline-flex items-center gap-2 text-sm font-semibold text-slate-300 hover:text-orange-500 transition-colors mt-8" data-testid="featured-website">
                  {featured.website.replace(/^https?:\/\/(www\.)?/, "").replace(/\/.*$/, "")} <ExternalLink className="w-3.5 h-3.5" />
                </a>
              )}
            </div>

            <div className="lg:col-span-2 p-8 lg:p-10">
              <h2 className="text-2xl sm:text-3xl font-black tracking-tight leading-snug">{featured.tagline}</h2>
              <p className="text-slate-400 mt-4 leading-relaxed">{featured.description}</p>

              {featured.name === "KATRES" && (
                <>
                  <div className="grid grid-cols-3 gap-px bg-slate-800 border border-slate-800 mt-8 text-center">
                    {[
                      { icon: CalendarClock, big: "1991", small: "Anul fondării" },
                      { icon: Gauge, big: "35+", small: "Ani de experiență" },
                      { icon: Globe2, big: "Europa", small: "Instalații internaționale" },
                    ].map((s) => (
                      <div key={s.small} className="bg-slate-950 py-5 px-2">
                        <s.icon className="w-5 h-5 text-orange-500 mx-auto" />
                        <div className="text-xl font-black mt-2">{s.big}</div>
                        <div className="text-[11px] text-slate-500 uppercase tracking-wide mt-0.5">{s.small}</div>
                      </div>
                    ))}
                  </div>
                  <div className="flex flex-wrap gap-2 mt-7">
                    {katresChips.map((c) => (
                      <span key={c} className="text-xs font-semibold text-slate-300 border border-slate-700 px-3 py-1.5">{c}</span>
                    ))}
                  </div>
                </>
              )}

              <Link to="/contact" className="inline-flex items-center gap-2 mt-8 text-sm font-bold uppercase tracking-wide text-orange-500 hover:gap-3 transition-all" data-testid="featured-cta">
                Solicită o ofertă <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
          </div>
        </div>

        {/* Other partners */}
        {others.length > 0 && (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5 mt-5">
            {others.map((p) => (
              <div key={p.id} className="border border-slate-800 bg-slate-900/40 p-6 flex items-start gap-4" data-testid={`partner-${p.id}`}>
                <Cpu className="w-5 h-5 text-orange-500 mt-1 shrink-0" />
                <div>
                  <div className="font-black tracking-tight">{p.name}</div>
                  <p className="text-sm text-slate-400 mt-1">{p.tagline || p.description}</p>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </section>
  );
}
